/**
 * 企业端 — 司机管理（列表 + 新增 + 设置司机独立登录密码）
 * N6(2026-09-04)：司机独立登录闭环 —— 备案司机后可在此设置司机登录密码，
 * 司机凭 手机号+密码 走 POST /driver/auth/login 登录司机端。
 */
import { useEffect, useState } from 'react';
import { createDriver, fetchDrivers, setDriverPassword } from '@/api/transit';
import { Empty, Loading, PageHeader, StatusTag } from '@/components/common';
import type { TransitDriver } from '@/types';

export default function EntDrivers() {
  const [drivers, setDrivers] = useState<TransitDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', license_no: '', license_type: 'A2' });

  const load = () => {
    fetchDrivers().then((d) => {
      setDrivers(d.list);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Loading />;

  const submit = async () => {
    if (!form.name || !form.phone) {
      alert('请填写司机姓名与手机号');
      return;
    }
    await createDriver({ ...form, owner_id: 3001 });
    setShowForm(false);
    load();
  };

  const setupPassword = async (d: TransitDriver) => {
    const pwd = window.prompt(`为司机「${d.name}」设置登录密码（6-20 位）：`);
    if (!pwd) return;
    if (pwd.length < 6) {
      alert('密码至少 6 位');
      return;
    }
    try {
      await setDriverPassword(d.id, pwd);
      alert(`已为司机 ${d.name} 设置登录密码，司机可用手机号 ${d.phone} 登录司机端`);
    } catch (e) {
      alert(e instanceof Error ? e.message : '设置失败');
    }
  };

  return (
    <div>
      <PageHeader
        title="司机管理"
        sub="运输司机备案（驾驶证 / 准驾车型）"
        extra={
          <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? '收起表单' : '＋ 备案司机'}
          </button>
        }
      />

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">新增司机</div>
          <div className="grid grid-3">
            <div className="field">
              <label>姓名 *</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="field">
              <label>手机号 *</label>
              <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="field">
              <label>准驾车型</label>
              <select value={form.license_type} onChange={(e) => setForm((f) => ({ ...f, license_type: e.target.value }))}>
                <option>A2</option>
                <option>B2</option>
                <option>C1</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>驾驶证号</label>
            <input value={form.license_no} onChange={(e) => setForm((f) => ({ ...f, license_no: e.target.value }))} />
          </div>
          <button className="btn btn-primary" onClick={submit}>
            保存
          </button>
        </div>
      )}

      <div className="card">
        {drivers.length === 0 ? (
          <Empty />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>姓名</th>
                <th>手机号</th>
                <th>驾驶证号</th>
                <th>准驾车型</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.name}</strong>
                  </td>
                  <td>{d.phone}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.license_no || '—'}</td>
                  <td>{d.license_type || '—'}</td>
                  <td>
                    <StatusTag tone={d.status === 0 ? 'green' : 'muted'}>{d.status === 0 ? '正常' : '停用'}</StatusTag>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={() => setupPassword(d)} title="设置司机独立登录密码（司机端登录）">
                      设置登录密码
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
